'use client';

/**
 * ItemZoneNote (TRUS-697): "(12:00 PM EDT event time)" beside a time shown in
 * the viewer's zone, when the item was scheduled in a different zone. Renders
 * nothing when the zones agree at that instant (or the item's zone is unknown).
 */
import React from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { itemZoneTime } from '@/utils/zonedTime';

export type ItemZoneKind = 'event' | 'meeting' | 'task' | 'followUp' | 'scheduled';

export function useItemZoneNote() {
  const { t } = useAppTranslation();
  const viewer = useUserTimezone();
  return (value: string | number | Date | null | undefined, itemTimeZone?: string | null, kind: ItemZoneKind = 'event') => {
    if (value == null || value === '') return null;
    const time = itemZoneTime(value, viewer, itemTimeZone);
    if (!time) return null;
    switch (kind) {
      case 'meeting':
        return t('time.itemZone.meeting', '({{time}} meeting time)', { time });
      case 'task':
        return t('time.itemZone.task', '({{time}} task time)', { time });
      case 'followUp':
        return t('time.itemZone.followUp', '({{time}} follow-up time)', { time });
      case 'scheduled':
        return t('time.itemZone.scheduled', '({{time}} as scheduled)', { time });
      default:
        return t('time.itemZone.event', '({{time}} event time)', { time });
    }
  };
}

export function ItemZoneNote({
  value,
  itemTimeZone,
  kind = 'event',
  className,
}: {
  value: string | number | Date | null | undefined;
  itemTimeZone?: string | null;
  kind?: ItemZoneKind;
  className?: string;
}) {
  const note = useItemZoneNote()(value, itemTimeZone, kind);
  if (!note) return null;
  return (
    <span className={className} data-testid="item-zone-note">
      {note}
    </span>
  );
}

export default ItemZoneNote;
